import type { FrontifyImageContent } from '@/contentful';
import { ViewportSize } from '@/styles/viewportSize';

type Sizes = {
  xs: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
};

type HTMLImageProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
>;

type BaseProps = Omit<HTMLImageProps, 'src' | 'sizes'> & {
  src: FrontifyImageContent;
  sizes: Sizes;
  priority?: boolean;
};

type DecorativeImageProps = BaseProps & {
  decorative: boolean;
};

type RegularImageProps = BaseProps & {
  alt: string;
};

type Props = DecorativeImageProps | RegularImageProps;

/*
ResponsiveImage image utilty function is used to display the same image to all breakpoints, but with custom dimensions. 
This makes sure sure we're loading the most efficient file size for a given device 
Documentation: https://developer.mozilla.org/en-US/docs/Web/HTML/Guides/Responsive_images#art_direction
*/
export default function ResponsiveImage(props: Props) {
  // biome-ignore lint/suspicious/noImplicitAnyLet: Automated migration to Biome
  let alt, role;

  if ('decorative' in props) {
    alt = '';
    role = 'presentation';
  } else {
    alt = props.alt;
    role = props.role;
  }

  return (
    // biome-ignore lint/performance/noImgElement: Automated migration to Biome
    <img
      {...props}
      src={getImageUrl(props.src, props.sizes.xs)}
      srcSet={generateSrcSetString(props.src, props.sizes)}
      sizes={generateSizesString(props.sizes)}
      alt={alt}
      role={role}
      fetchPriority={props.priority ? 'high' : 'auto'}
      loading={props.priority ? 'eager' : 'lazy'}
    />
  );
}

const getImageUrl = (src: FrontifyImageContent, width: number) => {
  const urlObject = new URL(src.preview_url);
  urlObject.searchParams.set('width', width.toString());
  urlObject.searchParams.set('format', 'webp');

  return urlObject.toString();
};

const dpi = [1, 2, 3];

const generateSrcSetString = (
  src: FrontifyImageContent,
  { xs, sm, md, lg, xl }: Sizes
) => {
  const sizesEntries: string[] = [];

  dpi.forEach((entry: number) => {
    if (xl) sizesEntries.push(`${getImageUrl(src, entry * xl)} ${entry * xl}w`);
    if (lg) sizesEntries.push(`${getImageUrl(src, entry * lg)} ${entry * lg}w`);
    if (md) sizesEntries.push(`${getImageUrl(src, entry * md)} ${entry * md}w`);
    if (sm) sizesEntries.push(`${getImageUrl(src, entry * sm)} ${entry * sm}w`);
    sizesEntries.push(`${getImageUrl(src, entry * xs)} ${entry * xs}w`);
  });

  return sizesEntries.join(', ').trimEnd();
};

const generateSizesString = ({ xs, sm, md, lg, xl }: Sizes) => {
  const sizesEntries = [];

  if (xl) sizesEntries.push(`(min-width: ${ViewportSize.xl}px) ${xl}w`);
  if (lg) sizesEntries.push(`(min-width: ${ViewportSize.lg}px) ${lg}w`);
  if (md) sizesEntries.push(`(min-width: ${ViewportSize.md}px) ${md}w`);
  if (sm) sizesEntries.push(`(min-width: ${ViewportSize.sm}px) ${sm}w`);
  sizesEntries.push(`(min-width: ${ViewportSize.xs}px) ${xs}w`);

  return sizesEntries.join(', ').trimEnd();
};
